/**
 * KPI wording and units, as AgencyAnalytics words them. Values are stored in
 * the metric's own unit (cents for money, fractions for rates); people type
 * rands and percentages, so the modal converts both ways. "Increase by" and
 * "Decrease by" are always plain percentages.
 */

import { formatValue } from '../reports/format.ts';
import type { CatalogueMetric, MetricFormat } from '../reports/reports-api.ts';
import type {
  AlertCondition,
  AlertInterval,
  Kpi,
  KpiStatus,
  TargetCondition,
  TargetPeriod,
} from './kpis-api.ts';

export const TARGET_CONDITIONS: [TargetCondition, string][] = [
  ['GTE', 'Greater than or equal to'],
  ['LTE', 'Less than or equal to'],
  ['EQ', 'Exactly equal to'],
  ['INCREASE_BY', 'Increase by'],
  ['DECREASE_BY', 'Decrease by'],
];

export const ALERT_CONDITIONS: [AlertCondition, string][] = [
  ['GT', 'Greater Than'],
  ['GTE', 'Greater Than Or Equal To'],
  ['LT', 'Less Than'],
  ['LTE', 'Less Than Or Equal To'],
  ['EQ', 'Exactly Equal To'],
  ['INCREASE_BY', 'Increase By'],
  ['DECREASE_BY', 'Decrease By'],
];

export const TARGET_PERIODS: [TargetPeriod, string][] = [
  ['DAILY', 'Repeats daily'],
  ['WEEKLY', 'Repeats weekly'],
  ['MONTHLY', 'Repeats monthly'],
  ['QUARTERLY', 'Repeats quarterly'],
  ['ANNUALLY', 'Repeats annually'],
];

export const ALERT_INTERVALS: [AlertInterval, string][] = [
  ['DAY', 'Day'],
  ['WEEK', 'Week'],
  ['MONTH', 'Month'],
  ['QUARTER', 'Quarter'],
  ['ANNUAL', 'Annual'],
];

const SYMBOL: Record<string, string> = { GT: '>', GTE: '≥', LT: '<', LTE: '≤', EQ: '=' };
const CENTS = 100;
const PERCENT = 100;

export function isRelative(condition: string | null): boolean {
  return condition === 'INCREASE_BY' || condition === 'DECREASE_BY';
}

/** What a person types (rands, percentages) → the stored unit. */
export function toStored(typed: number, format: MetricFormat, condition: string): number {
  if (isRelative(condition)) return typed;
  if (format === 'money') return Math.round(typed * CENTS);
  if (format === 'percent') return typed / PERCENT;
  return typed;
}

/** The stored unit → what the field shows. */
export function toTyped(stored: number, format: MetricFormat, condition: string): number {
  if (isRelative(condition)) return stored;
  if (format === 'money') return stored / CENTS;
  if (format === 'percent') return Number((stored * PERCENT).toFixed(4));
  return stored;
}

export function unitHint(format: MetricFormat, condition: string): string {
  if (isRelative(condition)) return '% on the previous period';
  if (format === 'money') return 'Rand';
  if (format === 'percent') return '%';
  return '';
}

export function conditionText(
  condition: string | null,
  value: number | null,
  metric: CatalogueMetric | undefined,
): string {
  if (!condition || value === null) return '–';
  if (condition === 'INCREASE_BY') return `+${value}%`;
  if (condition === 'DECREASE_BY') return `−${value}%`;
  return `${SYMBOL[condition] ?? ''} ${formatValue(value, metric?.format ?? 'number')}`;
}

export const STATUS_LABEL: Record<KpiStatus, string> = {
  ON_TRACK: 'On track',
  OFF_TRACK: 'Off track',
  MET: 'Met',
  MISSED: 'Missed',
  NO_DATA: 'No data yet',
  ERROR: 'Could not refresh',
};

export function statusTone(status: KpiStatus | null): 'ok' | 'bad' | 'none' {
  if (status === 'ON_TRACK' || status === 'MET') return 'ok';
  if (status === 'OFF_TRACK' || status === 'MISSED') return 'bad';
  return 'none';
}

function aimsLow(condition: string | null): boolean {
  return condition === 'LTE' || condition === 'DECREASE_BY';
}

/** "71% worse than expected" — against the prorated pace, signed so better is good news. */
export function paceText(kpi: Kpi): string | null {
  if (kpi.last_value === null || !kpi.last_expected) return null;
  const ratio = kpi.last_value / kpi.last_expected - 1;
  const better = aimsLow(kpi.target_condition) ? ratio < 0 : ratio > 0;
  const percent = Math.round(Math.abs(ratio) * PERCENT);
  if (percent === 0) return 'Exactly as expected';
  return `${percent}% ${better ? 'better' : 'worse'} than expected`;
}

/** Share of the target reached, 0–1, for the progress bar. */
export function progressShare(kpi: Kpi): number {
  if (kpi.last_value === null || !kpi.last_target) return 0;
  return Math.min(1, Math.max(0, kpi.last_value / kpi.last_target));
}

export function periodText(kpi: Kpi): string {
  if (!kpi.last_period_from || !kpi.last_period_to) return '';
  const day = new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  const from = day.format(new Date(`${kpi.last_period_from}T00:00:00Z`));
  const to = day.format(new Date(`${kpi.last_period_to}T00:00:00Z`));
  return from === to ? from : `${from} – ${to}`;
}
